// ── Profile section (Wave 9, FR-PART-003 self-edit allowlist) ───────────────
// The participant may edit exactly name / phone / timezone /
// notification_preferences. Company/cohort/program/role/scope/report/capstone/
// certificate/status/email/title are never editable here; the server re-checks.
//
// ISOLATION (FR-PDASH-001): reads ONLY the participant's OWN /auth/me projection.
// REPORT-EXCLUSION (FR-REPORT-001): no report fields appear.

import { useState, type FormEvent, type ReactNode } from "react";
import { Button, Card, Checkbox, IconCog, Input } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import type { CoachingProfileDto, ProfileNotificationPreferences, ProfileUpdateRequest } from "../../services/coaching-dtos";
import { guardParticipantSelfEdit, isParticipantSelfEditEnabled } from "../../domain/participant-self-edit";
import { useProfileUpdateMutation } from "../../hooks/useCoachingQueries";
import { ParticipantSectionShell } from "./participant-section-shell";
import { orDash, participantQueryKeys, readOwnProfile } from "./participant-dashboard-helpers";

export function ParticipantProfileSection() {
  const profileQuery = useHonestQuery<CoachingProfileDto>(
    participantQueryKeys.profile(),
    readOwnProfile
  );
  const profile = profileQuery.data;
  const editEnabled = isParticipantSelfEditEnabled();
  const updateProfile = useProfileUpdateMutation();

  return (
    <ParticipantSectionShell
      title="Profile"
      subtitle="Your profile details."
      posture={profileQuery.posture}
      gate={profileQuery.gate}
      reference={profileQuery.reference}
      onRetry={profileQuery.retry}
      icon={<IconCog size={22} />}
      testId="participant-profile-section"
    >
      <div className="space-y-4">
        {editEnabled ? (
          <ParticipantProfileForm
            profile={profile}
            saving={updateProfile.isPending}
            onSubmit={(body) => updateProfile.mutate(body)}
          />
        ) : (
          <Card className="shadow-none">
            <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              <Field label="Name">{orDash(profile?.name)}</Field>
              <Field label="Email">{orDash(profile?.email)}</Field>
              <Field label="Phone">{orDash(profile?.phone)}</Field>
              <Field label="Timezone">{orDash(profile?.timezone)}</Field>
            </div>
          </Card>
        )}
      </div>
    </ParticipantSectionShell>
  );
}

interface ParticipantProfileFormProps {
  profile: CoachingProfileDto | undefined;
  saving: boolean;
  onSubmit: (body: ProfileUpdateRequest) => void;
}

function ParticipantProfileForm({ profile, saving, onSubmit }: ParticipantProfileFormProps) {
  const [form, setForm] = useState(() => formStateFromProfile(profile));

  // Re-seed the form when the profile object changes — render adjustment
  // (bug-pack class, 2026-07-06).
  const [prevProfile, setPrevProfile] = useState(profile);
  if (profile !== prevProfile) {
    setPrevProfile(profile);
    setForm(formStateFromProfile(profile));
  }

  function updatePreference(key: keyof ProfileNotificationPreferences, checked: boolean) {
    setForm((current) => ({
      ...current,
      notification_preferences: {
        ...current.notification_preferences,
        [key]: checked
      }
    }));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const proposed: Record<string, unknown> = { ...form };
    const guarded = guardParticipantSelfEdit(proposed);
    onSubmit(guarded.safe as ProfileUpdateRequest);
  }

  return (
    <form className="space-y-4" data-testid="participant-profile-form" onSubmit={submit}>
      <Card className="shadow-none">
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <Input
            label="Name"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            autoComplete="name"
          />
          <Field label="Email">{orDash(profile?.email)}</Field>
          <Input
            label="Phone"
            value={form.phone}
            onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
            autoComplete="tel"
          />
          <Input
            label="Timezone"
            value={form.timezone}
            onChange={(event) => setForm((current) => ({ ...current, timezone: event.target.value }))}
            autoComplete="off"
          />
        </div>
      </Card>

      <fieldset className="grid gap-3 rounded-lg border border-line px-4 py-3 sm:grid-cols-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Notification preferences
        </legend>
        <Checkbox
          label="Session reminders"
          checked={form.notification_preferences.session_reminders}
          onChange={(event) => updatePreference("session_reminders", event.target.checked)}
        />
        <Checkbox
          label="Calendar alerts"
          checked={form.notification_preferences.calendar_alerts}
          onChange={(event) => updatePreference("calendar_alerts", event.target.checked)}
        />
        <Checkbox
          label="Weekly digest"
          checked={form.notification_preferences.weekly_digest}
          onChange={(event) => updatePreference("weekly_digest", event.target.checked)}
        />
      </fieldset>

      <div className="flex justify-end">
        <Button type="submit" variant="primary" isLoading={saving}>
          Save
        </Button>
      </div>
    </form>
  );
}

function formStateFromProfile(profile: CoachingProfileDto | undefined): ProfileUpdateRequest {
  return {
    name: profile?.name ?? "",
    phone: profile?.phone ?? "",
    timezone: profile?.timezone ?? "",
    notification_preferences: {
      session_reminders: profile?.notification_preferences?.session_reminders ?? false,
      calendar_alerts: profile?.notification_preferences?.calendar_alerts ?? false,
      weekly_digest: profile?.notification_preferences?.weekly_digest ?? false
    }
  };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <p className="m-0 text-sm text-strong">{children}</p>
    </div>
  );
}
